import { Fragment, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useDataContext } from 'context/DataStoreContext';
import { useApi } from 'model/Api';
import { User } from 'model/User';
import { Ledger, ledgerStatusList } from 'model/Ledger';
import { Contractor } from 'model/Contractor';
import { generalStyle } from 'helper/Style';
import { convertCosts, convertDate, convertUserName } from 'helper/Converter';
import Layout from 'component/part/Layout';
import BackLink from 'component/part/BackLink';
import H2 from 'component/part/H2';
import Modal, { modalStyle } from 'component/part/Modal';
import List, { ListIndex, ListSearchApi } from 'component/part/List';

type ListLedger = Ledger & {
  leaderName: string;
  safetyOfficerName: string;
  primeContractorName: string;
  totalCosts: ReturnType<typeof convertCosts>,
  grossProfit: number;
};

const ledgerConditions = { with: ['cost'] };

/** 「完了」「失注」を除いた値。デフォルトでこの4つにチェックされた状態で絞り込みが適用される */
const defaultStatuses: number[] = ledgerStatusList
  .filter(({ label }) => label !== '完了' && label !== '失注')
  .map(({ value }) => value);

const defaultLedgerConditions = { status: defaultStatuses };

const ledgerIndexes: ListIndex[] = [
  { label: '現場名', key: 'name' },
  { label: '発注元', key: 'orderingParty' },
  { label: '略称', key: 'shortName' },
  { label: '上長', key: 'safetyOfficerId' },
  { label: '現場代理人', key: 'leaderId' },
  { label: '弊社次数', key: 'order' },
  { label: '元請', key: 'primeContractorId' },
  { label: '請負協力業者' },
  { label: '工期', key: 'endDate' },
  { label: '請負金額', key: 'price', align: 'right' },
  { label: '人工', align: 'right' },
  { label: '機械', align: 'right' },
  { label: '産廃', align: 'right' },
  { label: '経費', align: 'right' },
  { label: '費用小計', align: 'right' },
  { label: '粗利', align: 'right' },
];

const style = {
  table: {
    whiteSpace: 'nowrap' as const,
    fontSize: '1.4rem',
  },
  searchTrigger: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  dialog: {
    minWidth: '32rem',
    maxWidth: '90vw',
  },
  dialogTitle: {
    margin: 0,
    marginBottom: '1.6rem',
    fontSize: '1.6rem',
    fontWeight: 700,
  },
  dialogRow: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.4rem',
    marginBottom: '1.6rem',
  },
  dialogLabel: {
    fontSize: '1.2rem',
    fontWeight: 700,
  },
  dialogTextInput: {
    border: 'none',
    borderBottom: 'solid 0.1rem #cccccc',
    background: 'none',
    lineHeight: '2.4rem',
    fontSize: '1.6rem',
  },
  statusList: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.8rem 1.6rem',
  },
  statusItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.4rem',
    fontSize: '1.4rem',
    cursor: 'pointer',
  },
};

const ScheduleSearchForm: React.FC<{
  initialConditions: ListSearchApi['conditions'];
  onSearch: ListSearchApi['search'];
}> = ({ initialConditions, onSearch }) => {
  const initialWord = (initialConditions?.word as string | undefined) ?? '';
  const initialStatuses = (initialConditions?.status as number[] | undefined) ?? defaultStatuses;

  const [open, setOpen] = useState(false);
  const [word, setWord] = useState(initialWord);
  const [statuses, setStatuses] = useState<number[]>(initialStatuses);

  // ダイアログを開くたびに、最後にsubmitされた条件で再初期化する（キャンセル後の再開でも整合させる）
  useEffect(() => {
    if (open) {
      setWord(initialWord);
      setStatuses(initialStatuses);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleStatus = (value: number) => {
    setStatuses((prev) => prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const conditions: NonNullable<ListSearchApi['conditions']> = {};
    const trimmed = word.trim();
    if (trimmed) conditions.word = trimmed;
    if (statuses.length > 0) conditions.status = [...statuses].sort((a, b) => a - b);
    onSearch(conditions);
    setOpen(false);
  };

  return <Fragment>
    <div css={style.searchTrigger}>
      <button type='button' onClick={() => setOpen(true)} css={generalStyle.button('info')}>
        絞り込み
      </button>
    </div>
    <Modal display={open} onHide={() => setOpen(false)}>
      <form onSubmit={handleSubmit} css={style.dialog}>
        <h3 css={style.dialogTitle}>絞り込み検索</h3>
        <div css={style.dialogRow}>
          <span css={style.dialogLabel}>現場名・略称</span>
          <input
            type='text'
            value={word}
            onChange={(e) => setWord(e.target.value)}
            placeholder='ワードを入力'
            css={style.dialogTextInput}
          />
        </div>
        <div css={style.dialogRow}>
          <span css={style.dialogLabel}>ステータス</span>
          <div css={style.statusList}>
            {ledgerStatusList.map(({ value, label }) => (
              <label key={value} css={style.statusItem}>
                <input
                  type='checkbox'
                  checked={statuses.includes(value)}
                  onChange={() => toggleStatus(value)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
        </div>
        <div css={modalStyle.buttonArea}>
          <button type='submit' css={generalStyle.button()}>検索</button>
          <button type='button' onClick={() => setOpen(false)} css={generalStyle.button('warning')}>
            キャンセル
          </button>
        </div>
      </form>
    </Modal>
  </Fragment>;
};

const ScheduleList: React.FC = () => {
  const { state } = useDataContext();
  const { list = [] } = state?.schedule || {};
  const api = useApi();
  const [users, setUsers] = useState<User[]>([]);
  const [ledgers, setLedgers] = useState<ListLedger[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);

  useEffect(() => {
    (async () => {
      const [usersResponse, contractorsResponse] = await Promise.all([
        api.user.getsByOwn(),
        api.contractor.getsByOwn(),
      ]);
      setUsers(usersResponse);
      setContractors(contractorsResponse);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      const listLedgers: ListLedger[] = list.map((ledger) => {
        const leader = users.find((user) => user.id === ledger.leaderId);
        const safetyOfficer = users.find((user) => user.id === ledger.safetyOfficerId);
        const contractor = contractors.find((contractor) => contractor.id === ledger.primeContractorId);
        const totalCosts = convertCosts(ledger.costs);

        return {
          ...ledger,
          leaderName: leader ? convertUserName(leader) : '',
          safetyOfficerName: safetyOfficer ? convertUserName(safetyOfficer) : '',
          primeContractorName: contractor?.name || '',
          totalCosts,
          grossProfit: ledger.price - totalCosts.total,
        };
      });
      setLedgers(listLedgers);
    })();
  }, [list]);

  const rows = ledgers.map((ledger) => [
    <NavLink to={`/ledger/${ledger.id}`} css={generalStyle.link}>{ledger.name}</NavLink>,
    ledger.orderingParty,
    ledger.shortName,
    ledger.safetyOfficerName,
    ledger.leaderName,
    ledger.order ? `${ledger.order}次` : '元請',
    ledger.primeContractorName,
    (ledger.members || []).map((partner, i) => <div key={i}>{partner.name || ''}</div>),
    `${convertDate(ledger.startDate, 'list')}〜${convertDate(ledger.endDate, 'list')}`,
    `¥${ledger.price.toLocaleString()}`,
    `¥${ledger.totalCosts.personal.toLocaleString()}`,
    `¥${ledger.totalCosts.macinery.toLocaleString()}`,
    `¥${ledger.totalCosts.industrialWaste.toLocaleString()}`,
    `¥${ledger.totalCosts.other.toLocaleString()}`,
    `¥${ledger.totalCosts.total.toLocaleString()}`,
    `¥${ledger.grossProfit.toLocaleString()}`,
  ]);

  return <Layout>
    <BackLink link='/' label='トップ'></BackLink>
    <H2>工事着工表</H2>
    <div css={generalStyle.listLayout}>
      <List
        type='schedule'
        conditions={ledgerConditions}
        defaultConditions={defaultLedgerConditions}
        indexes={ledgerIndexes}
        stickyColumns={1}
        rows={rows}
        css={style.table}
      >
        {({ search, conditions }) => (
          <ScheduleSearchForm initialConditions={conditions} onSearch={search} />
        )}
      </List>
    </div>
  </Layout>;
};

export default ScheduleList;
